"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import { useState } from "react";

export function WaitlistForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          source: "auth-waitlist",
        }),
      });

      const isJsonResponse =
        response.headers.get("content-type")?.includes("application/json") ?? false;

      const payload = (isJsonResponse ? await response.json() : null) as {
        error?: string;
        message?: string;
      } | null;

      if (!response.ok) {
        const fallbackMessage = isJsonResponse
          ? "Unable to join waitlist"
          : "Unexpected response from the server. Please try again.";
        throw new Error(payload?.error ?? fallbackMessage);
      }

      setSuccessMessage(payload?.message ?? "You are on the waitlist.");
      setEmail("");
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex shadow-vl flex-col gap-6", className)} {...props}>
      <Card className="border-none rounded-2xl p-2">
        <CardHeader className="pb-8">
          <CardTitle className="text-xl text-center">Join the waitlist</CardTitle>
          <CardDescription className="text-center">
            Leave your email and we&apos;ll let you know when new access opens up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleJoinWaitlist}>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Joining waitlist..." : "Join waitlist"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                By joining, you agree to our{" "}
                <Link target="_blank" href="https://super.degree/terms" className="underline underline-offset-4 hover:text-foreground">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link target="_blank" href="https://super.degree/privacy" className="underline underline-offset-4 hover:text-foreground">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="mt-4 text-center text-sm">
              Already have an account?{" "}
              <Link href="/auth/login" className="underline underline-offset-4">
                Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
